import React, { Component } from 'react';
import type { Dispatch } from 'redux';

import {
    createShortcutEvent,
    createToolbarEvent,
    sendAnalytics
} from '../../../analytics';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any.js';
import { isDeviceLandScape } from '../../../base/environment/environment.js';
import {
    getScreenSizeCategory,
    getWindowHeight,
    getWindowWidth
} from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { Icon, IconMenuDown, IconMenuUp } from '../../../base/icons';
import { participantJoined } from '../../../base/participants/actions.js';
import { getAllParticipantAndSharedFrames, getLocalParticipant } from '../../../base/participants/functions.js';
import { connect } from '../../../base/redux';
import ConnectionLostOverlay from '../../../connection-indicator/components/web/ConnectionLostOverlay.js';
import { open } from '../../../participants-pane/actions.js';
import Toolbox from '../../../toolbox/components/web/Toolbox.js';
import { isButtonEnabled } from '../../../toolbox/functions.web';
import {
    LAYOUTS,
    getCurrentLayout
} from '../../../video-layout';
import { setFilmstripVisible } from '../../actions';
import { setHideHeaderFooter } from '../../actions.native.js';
import { selectParticipantforLargeViewGrid } from '../../functions.web.js';

import SharedThumbnail from './SharedThumbnail';
import SharedVideoThumbnail from './SharedVideoThumbnail';

declare var APP: Object;
declare var interfaceConfig: Object;

/**
 * The type of the React {@code Component} props of {@link Filmstrip}.
 */
type Props = {

    /**
     * Additional CSS class names to add to the root.
     */
    _className: string,

    /**
     * The current layout of the filmstrip.
     */
    _currentLayout: string,

    /**
     * The number of columns in tile view.
     */
    _columns: number,

    /**
     * The filmstrip view orientation ('Horizontal' | 'Vertical').
     */
    _filmStripView: string,

    /**
     * The width of the filmstrip.
     */
    _filmstripWidth: number,

    /**
     * Whether the filmstrip scrollbar should be hidden or not.
     */
    _hideScrollbar: boolean,

    /**
     * Whether the filmstrip toolbar should be hidden or not.
     */
    _hideToolbar: boolean,

    /**
     * Whether the filmstrip button is enabled.
     */
    _isFilmstripButtonEnabled: boolean,

    /**
     * Number of participants in the call.
     */
    _participantsLength: number,

    /**
     * The number of rows in tile view.
     */
    _rows: number,

    /**
     * Additional CSS class names to add to the container of all the thumbnails.
     */
    _videosClassName: string,

    /**
     * Whether or not the filmstrip videos should currently be displayed.
     */
    _visible: boolean,

    /**
     * The redux {@code dispatch} function.
     */
    dispatch: Dispatch<any>,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    /**
     * Whether this instance is a recorder.
     */
    iAmRecorder: boolean,

    /**
     * TileView enabled state.
     */
    tileViewEnabled: boolean,

    /*
    * if whiteboard or etherpad thumbnail  is visible or not.
    */
    _isSharedThumbnailVisible: boolean,


};

/**
 * State type for the Filmstrip component.
 */
type State = {
    filmStripMaxWidth: ?number,
    filmStripMaxHeight: ?numberlike,
    screenWidth: number,
};

/**
 * Constants for responsive breakpoints and dimensions.
 */
const BREAKPOINTS = {
    SMALL: 450,
    MEDIUM: 1024
};

const LOCAL_VIDEO_WIDTHS = {
    SMALL: 195, // 160 + 35
    MEDIUM: 240, // 200 + 40
    LARGE: 340 // 294 + 40
};


/**
 * Implements a React {@link Component} which represents the filmstrip on
 * Web/React.
 *
 * @augments Component
 */
class Filmstrip extends Component<Props, State> {
    _remoteVideosRef: Object;
    isDown: boolean;
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;

    /**
     * Initializes a new {@code Filmstrip} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            filmStripMaxWidth: null,
            filmStripMaxHeight: null,
            screenWidth: typeof window === 'undefined' ? 1024 : getWindowWidth(),
            screenHeight: typeof window === 'undefined' ? 858 : getWindowHeight(),
            windowWidth: getWindowWidth()
        };

        // Bind event handlers so they are only bound once for every instance.
        this.generateParticipants = this.generateParticipants.bind(this);
        this._onShortcutToggleFilmstrip = this._onShortcutToggleFilmstrip.bind(this);
        this._onToolbarToggleFilmstrip = this._onToolbarToggleFilmstrip.bind(this);
        this._onHandleHeaderFooter = this._onHandleHeaderFooter.bind(this);

        this._handleResize = this._handleResize.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.tileLimits = [ 5, 7, 9, 14, 17, 20, 27 ];

        this._onMouseDown = this._onMouseDown.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
        this._onMouseUp = this._onMouseUp.bind(this);
        this._onMouseMove = this._onMouseMove.bind(this);

        this._remoteVideosRef = React.createRef();
        this.isDown = false;
        this.startX = 0;
        this.startY = 0;
        this.scrollLeft = 0;
        this.scrollTop = 0;
    }

    /**
     * Handles mouse down events for scrolling the filmstrip when dragging.
     * @param {*} e - The mouse down event.
     * @returns {void}
     */
    _onMouseDown(e) {
        if (e.button !== 0 || !this._remoteVideosRef.current) {
            return;
        }
        this.isDown = true;
        this.startX = e.pageX - this._remoteVideosRef.current.offsetLeft;
        this.startY = e.pageY - this._remoteVideosRef.current.offsetTop;
        this.scrollLeft = this._remoteVideosRef.current.scrollLeft;
        this.scrollTop = this._remoteVideosRef.current.scrollTop;
    }

    /**
     * Handles mouse leave events for scrolling the filmstrip when dragging.
     */
    _onMouseLeave() {
        this.isDown = false;
    }

    /**
     * Handles mouse up events for scrolling the filmstrip when dragging.
     */
    _onMouseUp() {
        this.isDown = false;
    }

    /**
     * Handles mouse move events for scrolling the filmstrip when dragging.
     * @param {*} e - The mouse move event.
     * @returns {void}
     */
    _onMouseMove(e) {
        if (!this.isDown || !this._remoteVideosRef.current) {
            return;
        }
        e.preventDefault();
        const x = e.pageX - this._remoteVideosRef.current.offsetLeft;
        const y = e.pageY - this._remoteVideosRef.current.offsetTop;
        const walkX = x - this.startX;
        const walkY = y - this.startY;

        this._remoteVideosRef.current.scrollLeft = this.scrollLeft - walkX;
        this._remoteVideosRef.current.scrollTop = this.scrollTop - walkY;
    }

    /**
     * Calls into legacy UI to update the application layout, if necessary.
     *
     * @inheritdoc
     * returns {void}
     */
    componentDidUpdate(prevProps) {
        if (this.props._pictureInPictureEnabled !== prevProps._pictureInPictureEnabled) {
            window.addEventListener('resize', this.handleResize);

            // Clear inline styles when entering picture-in-picture mode so
            // the grid/tiles revert to their default CSS rules.
            if (this.props._pictureInPictureEnabled && !prevProps._pictureInPictureEnabled) {
                this._clearParticipantInlineStyles();
                this.handleResize();
            } else {
                this.handleResize();
            }
        }
    }

    /**
     * Implements React's {@link Component#componentDidMount}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        window.FilmstripInstance = this;

        // Register keyboard shortcut
        if (typeof APP !== 'undefined' && APP.keyboardshortcut) {
            APP.keyboardshortcut.registerShortcut(
                'F',
                'filmstripPopover',
                this._onShortcutToggleFilmstrip,
                'keyboardShortcuts.toggleFilmstrip'
            );
        }

        // Use event delegation for dynamically created more-participants-container
        this._onMoreParticipantsClick = e => {
            if (e.target.closest('#more-participants-container')) {
                this.props.dispatch(open());
            }
        };
        document.addEventListener('click', this._onMoreParticipantsClick);


        // Add resize listener
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this._handleResize);
            this._calculateDimensions();
        }

        // Auto-hide filmstrip for recorder if configured
        if (
            this.props.iAmRecorder
            && typeof interfaceConfig !== 'undefined'
            && interfaceConfig?.hideParticipantTilesOnRecording
        ) {
            this._onToolbarToggleFilmstrip();
        }
    }

    /**
   * Implements React's {@link Component#componentWillUnmount}.
   *
   * @inheritdoc
   */
    componentWillUnmount() {
        delete window.FilmstripInstance;

        // Unregister keyboard shortcut
        if (typeof APP !== 'undefined' && APP.keyboardshortcut) {
            APP.keyboardshortcut.unregisterShortcut('F');
        }

        // Remove resize listener
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this._handleResize);
        }
        window.removeEventListener('resize', this.handleResize);

        if (this._onMoreParticipantsClick) {
            document.removeEventListener('click', this._onMoreParticipantsClick);
        }
    }

    handleResize = _.debounce(callback => {
        const width = getWindowWidth();

        this.setState({
            windowWidth: width
        }, () => {
            if (typeof callback === 'function') {
                callback();
            }
        });
    }, 250);

    /**
     * Handle window resize events.
     *
     * @private
     * @returns {void}
     */
    _handleResize() {
        if (typeof window !== 'undefined') {
            this.setState(
                {
                    screenWidth: getWindowWidth(),
                    screenHeight: getWindowHeight()
                },
                () => {
                    this._calculateDimensions();
                }
            );
        }
    }


    // Fake Participants code starts here

    /**
     * Generates fake participants for testing purposes.
     *
     * @param {number} numberOfParticipants - The number of fake participants to generate.
     * @returns {Array<string>} Array of generated participant IDs.
     */
    generateParticipants(numberOfParticipants) {
        // Validate input
        if (!numberOfParticipants || numberOfParticipants <= 0) {
            return [];
        }

        // Check for conference availability
        const conference = this.props._conference
            || APP?.store?.getState()?.['features/base/conference']?.conference
            || APP?.conference?._room;

        if (!conference) {
            return [];
        }

        const generatedIds = [];

        for (let i = 1; i <= numberOfParticipants; i++) {
            // Generate unique ID with timestamp and random suffix to avoid collisions
            const id = `fake-${Date.now()}-${i}-${Math.random().toString(36)
.substr(2, 9)}`;

            // Dispatch participantJoined with correct participant structure
            this.props.dispatch(participantJoined({
                id,
                name: `Fake Participant ${i}`,
                email: `fake${i}@example.com`,
                conference,
                isFakeParticipant: true,
                connectionStatus: 'active',
                role: 'participant',
                color: 'red',
                dominantSpeaker: false
            }));

            generatedIds.push(id);
        }


        return generatedIds;
    }

    // Fake Participants code ends here

    /**
     * Calculate filmstrip dimensions based on screen size.
     *
     * @private
     * @returns {void}
     */
    _calculateDimensions() {
        const { screenHeight, windowWidth } = this.state;
        const localHeight = this._getLocalVideoHeight();
        const localVideoWidth = this._getLocalVideoWidth(windowWidth);
        const filmStripMaxWidth = Math.max(0, windowWidth - localVideoWidth);
        const filmStripMaxHeight = Math.max(0, screenHeight - localHeight);

        this.setState({
            filmStripMaxWidth,
            filmStripMaxHeight
        });
    }

    /**
     * Get local video width based on screen size.
     *
     * @private
     * @param {number} screenWidth - Current screen width.
     * @returns {number} Local video width.
     */
    _getLocalVideoWidth(screenWidth: number): number {
        if (screenWidth < BREAKPOINTS.SMALL) {
            return LOCAL_VIDEO_WIDTHS.SMALL;
        } else if (screenWidth < BREAKPOINTS.MEDIUM) {
            return LOCAL_VIDEO_WIDTHS.MEDIUM;
        }

        return LOCAL_VIDEO_WIDTHS.LARGE;
    }

    /**
     * Get local video height based on conference view.
     * @private
     * @returns {number} Local video height.
     */
    _getLocalVideoHeight() {
        const { _conferenceView } = this.props;

        if (_conferenceView === 'fixed') {
            return 178 + 65 + 65 + 25;
        }

        return 178 + 65;

    }

    /**
     * Get the number of columns for tile view based on participant count and screen size.
     *
     * @private
   * @param {number} participantCount - Number of participants.
     * @returns {number} Number of columns.
     */
    _getTileViewColumns(participantCount: number): number {
        const screenCategory = getScreenSizeCategory();
        const { windowWidth } = this.state;

        if (this.props._pictureInPictureEnabled) {
            return participantCount === 1 ? 1 : 2;
        }

        // Mobile screens (≤375px) - prioritize vertical stacking
        if (windowWidth <= 500 || getWindowWidth() <= 500) {
            if (participantCount === 1) {
                return 1;
            }
            if (participantCount === 2) {
                return 1; // Stack vertically for better visibility
            }
            if (participantCount <= 4) {
                return 2;
            }

            return 2; // Maximum 2 columns on mobile
        }

        if (participantCount === 3 && this.props.isPaneOpen && windowWidth <= 1000 && windowWidth >= 500) {
            return 2;
        }

        // Tablet screens (376px-900px) - balanced approach
        if (screenCategory === 'tablet' || (windowWidth > 500 && windowWidth <= 900)) {
            if (participantCount === 1) {
                return 1;
            }
            if (participantCount === 2) {
                return 2;
            }
            if (participantCount === 3) {
                return 3;
            }
            if (participantCount === 4) {
                return 2;
            }
            if (participantCount <= 9) {
                return 3;
            }

            return 4; // Maximum 4 columns on tablet
        }

        // Desktop screens (901px-max) - standard layout
        if (screenCategory === 'desktop' || (windowWidth > 900)) {
            switch (participantCount) {
            case 1:
                return 1;
            case 2:
                return 2;
            case 3:
                return 3;
            case 4:
                return 2;
            case 5:
            case 6:
                return 3;
            case 7:
            case 8:
                return 4;
            case 9:
            case 10:
                return 5;
            case 11:
            case 12:
                return 6;
            case 13:
            case 14:
            case 15:
                return 5;
            case 16:
            case 17:
            case 18:
                return 6;
            case 19:
            case 20:
            case 21:
                return 7;
            case 22:
            case 23:
            case 24:
                return 6;
            default:
                return 7;
            }
        }

    }

    /**
     * @param {boolean} hideHeaderFooter - true/false to hide header and footer.
     * @returns {{
     *     hideHeaderFooter: boolean
     * }}
     */
    _onHandleHeaderFooter() {
        if (isDeviceLandScape()) {
            this.props.dispatch(setHideHeaderFooter(!this.props.hideHeaderFooter));
        }
    }

    /**
     * Get filmstrip container styles based on current layout.
     *
     * @private
     * @returns {Object} Style object for the filmstrip container.
     */
    _getFilmstripContainerStyles(): Object {
        const {
            _currentLayout,
            _filmStripView,
            _participantsLength,
            isPaneOpen,
            _tileViewTileLimit,
            iAmRecorder,
            _isSharedThumbnailVisible
        } = this.props;
        const numberOfParticipants = parseInt(_isSharedThumbnailVisible ? _participantsLength + 1 : _participantsLength, 10) - (iAmRecorder ? 1 : 0);
        let finalParticipantCount;


        if (numberOfParticipants >= _tileViewTileLimit) {
            finalParticipantCount = _tileViewTileLimit + 1;
        } else {
            finalParticipantCount = numberOfParticipants;
        }

        const isHorizontal = _filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW;

        let styles = {
            display: 'grid',
            justifyContent: 'start',
            scrollbarWidth: 'thin'
        };

        switch (_currentLayout) {
        case LAYOUTS.VERTICAL_FILMSTRIP_VIEW:
            styles = {
                display: 'flex',
                flexDirection: isHorizontal ? 'row' : 'column',
                width: isHorizontal ? '100px' : ''
            };
            break;

        case LAYOUTS.TILE_VIEW: {
            if (!this.props._pictureInPictureEnabled) {
                return {};
            }

            const columns = this._getTileViewColumns(finalParticipantCount);

            styles = {
                ...styles,
                gridAutoFlow: isHorizontal ? 'column' : 'row',
                whiteSpace: 'nowrap',
                zIndex: 10,
                alignItems: numberOfParticipants === 3 ? 'start' : '',
                gridTemplateColumns: `repeat(${columns}, minmax(150px, 1fr))`,
                width: '100%',
                padding: numberOfParticipants <= 4 ? '0px 10px' : '10px',
                position: 'relative',
                margin: '0 0 0 0px',
                gap: numberOfParticipants < 4 && getWindowWidth() > 500 && !isPaneOpen ? '0px 10px' : '0px',
                height: '100%'
            };
            break;
        }

        default:
            styles.gridAutoFlow = isHorizontal ? 'column' : 'row';
        }

        return styles;
    }

    /**
     * Get CSS class names for the remote video container.
     *
     * @private
     * @returns {string} CSS class names.
     */
    _getRemoteVideoContainerClassName(): string {
        const { _filmStripView, _currentLayout, _rows, _columns } = this.props;

        let className = 'remote-videos-container';

        if (_filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW) {
            className += ' remote-videos-container-horizontal';
        } else if (_filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW) {
            className += ' remote-videos-container-vertical';
        }

        if (_currentLayout === LAYOUTS.TILE_VIEW && _rows > _columns) {
            className += ' has-overflow';
        }

        return className;
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const {
            _filmStripView,
            _hideScrollbar,
            _participantsLength,
            iAmRecorder,
            _localParticipant,
            _className,
            _hasYoutubeParticipant,
            _tileViewTileLimit
        } = this.props;

        const numberOfParticipants = _participantsLength - (iAmRecorder ? 1 : 0);
        const finalParticipantCount = _tileViewTileLimit && numberOfParticipants > _tileViewTileLimit
            ? _tileViewTileLimit + 1
            : numberOfParticipants;

        const limitParticipantCount = _tileViewTileLimit && numberOfParticipants > _tileViewTileLimit
            ? _hasYoutubeParticipant ? _tileViewTileLimit - 1 : _tileViewTileLimit
            : numberOfParticipants;

        let remoteVideosClassName = '';

        remoteVideosClassName = `participants-${finalParticipantCount} participants-limit-${limitParticipantCount}`;
        if (this.props._pictureInPictureEnabled) {
            remoteVideosClassName += ' pip-mode';
        }

        const filmstripContainerStyles = this._getFilmstripContainerStyles();
        const _videosClassName = {
            display: 'flex',
            flexDirection: `${_filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW ? 'row' : 'column'}`,
            marginBottom: `${_filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW ? '10px' : '0px'}`,
            overflowX: `${_filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW ? 'auto' : 'hidden'}`,
            overflowY: `${_filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW ? 'auto' : 'hidden'}`,
            height: `${_filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW ? '' : 'fit-content'}`
        };
        const filmstripLocalVideoStyles = {
            display: 'flex',
            flexDirection: _filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW ? 'row' : 'column'
        };
        const remoteVideoContainerClassName = this._getRemoteVideoContainerClassName();

        let remoteVideosWrapperClassName = 'filmstrip__videos';

        if (_hideScrollbar) {
            remoteVideosWrapperClassName += ' hide-scrollbar';
        }

        const filmstripRemoteVideosStyles = {
            overflowY: _filmStripView === LAYOUTS.TILE_VIEW && (_participantsLength > 4) ? 'auto' : ''
        };

        const filmstripReduceHeightStyles = {
            width: '100%',
            height: '100%'
        };

        return (
            <div
                className = { `filmstrip ${_className}` }
                style = { filmstripReduceHeightStyles }>
                {_filmStripView === LAYOUTS.TILE_VIEW
                    && <ConnectionLostOverlay
                        _participant = { _localParticipant }
                        filmStripView = { _filmStripView } />}
                {this.props._conferenceView === 'float' && _filmStripView === LAYOUTS.TILE_VIEW ? (
                    <Toolbox
                        _isOpen = { this.props.isPaneOpen }
                        hview = { this.props._conferenceView === 'float' ? 'float' : null }
                        roomName = { this.props._roomName } />) : null}

                <div
                    className = { remoteVideosClassName }
                    id = 'remoteVideos'
                    onMouseDown = { this._onMouseDown }
                    onMouseLeave = { this._onMouseLeave }
                    onMouseMove = { this._onMouseMove }
                    onMouseUp = { this._onMouseUp }
                    ref = { this._remoteVideosRef }
                    style = { _videosClassName }>
                    {/* Local video container */}
                    <div
                        className = 'filmstrip__videos'
                        id = 'filmstripLocalVideo'
                        style = { filmstripLocalVideoStyles }>
                        <div id = 'filmstripLocalVideoThumbnail' />
                        {!iAmRecorder && _filmStripView !== LAYOUTS.TILE_VIEW && <SharedThumbnail dispatch = { this.props.dispatch } />}
                        {!iAmRecorder && _filmStripView !== LAYOUTS.TILE_VIEW && <SharedVideoThumbnail />}
                    </div>

                    <div
                        className = { remoteVideosWrapperClassName }
                        id = 'filmstripRemoteVideos'
                        onClick = { this._onHandleHeaderFooter }
                        style = { filmstripRemoteVideosStyles }>
                        <div
                            className = { remoteVideoContainerClassName }
                            id = 'filmstripRemoteVideosContainer'
                            style = { filmstripContainerStyles }>
                            {iAmRecorder ? (
                                <>
                                    <SharedThumbnail dispatch = { this.props.dispatch } />
                                    <SharedVideoThumbnail />
                                </>
                            )
                                : <>
                                    <div
                                        className = { _filmStripView === LAYOUTS.TILE_VIEW ? 'videocontainer' : '' }
                                        id = 'localVideoTileViewContainer' />
                                    {_filmStripView === LAYOUTS.TILE_VIEW && <SharedThumbnail dispatch = { this.props.dispatch } />}
                                    {_filmStripView === LAYOUTS.TILE_VIEW && <SharedVideoThumbnail />}
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
   * Dispatches an action to change the visibility of the filmstrip.
   *
   * @private
   * @returns {void}
   */
    _doToggleFilmstrip() {
        this.props.dispatch(setFilmstripVisible(!this.props._visible));
    }

    /**
     * Creates an analytics keyboard shortcut event and dispatches an action for
     * toggling filmstrip visibility.
     *
     * @private
     * @returns {void}
     */
    _onShortcutToggleFilmstrip() {
        sendAnalytics(
            createShortcutEvent('toggle.filmstrip', { enable: this.props._visible })
        );

        this._doToggleFilmstrip();
    }

    /**
     * Creates an analytics toolbar event and dispatches an action for
     * toggling filmstrip visibility.
     *
     * @private
     * @returns {void}
     */
    _onToolbarToggleFilmstrip() {
        sendAnalytics(
            createToolbarEvent('toggle.filmstrip.button', {
                enable: this.props._visible
            })
        );

        this._doToggleFilmstrip();
    }

    /**
     * Creates a React Element for changing the visibility of the filmstrip when
     * clicked.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderToggleButton() {
        const { _visible, t } = this.props;
        const icon = _visible ? IconMenuDown : IconMenuUp;

        return (
            <div className = 'filmstrip__toolbar'>
                <button
                    aria-label = { t('toolbar.accessibilityLabel.toggleFilmstrip') }
                    id = 'toggleFilmstripButton'
                    onClick = { this._onToolbarToggleFilmstrip }
                    type = 'button'>
                    <Icon src = { icon } />
                </button>
            </div>
        );
    }

    /**
     * Remove inline styles applied to participant tiles (grid-row, grid-column, height).
     * This helps when entering picture-in-picture mode to let CSS control layout again.
     *
     * @private
     * @returns {void}
     */
    _clearParticipantInlineStyles() {
        const parentContainer = document.querySelector('#filmstripRemoteVideosContainer');

        if (!parentContainer) {
            return;
        }

        const participantDiv = parentContainer.children;

        for (let i = 0; i < participantDiv.length; i++) {
            const el = participantDiv[i];

            if (!el) {
                return;
            }

            // If this tile contains the local inner container, clear its inline height
            const localInner = el.querySelector('#localVideoContainer');

            if (localInner) {
                localInner.style.removeProperty('height');
            }

            el.style.removeProperty('grid-row');
            el.style.removeProperty('grid-column');
            el.style.removeProperty('grid-row-start');
            el.style.removeProperty('grid-column-start');
            el.style.removeProperty('height');
        }
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code Filmstrip}'s props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const { iAmSipGateway, iAmRecorder } = state['features/base/config'];
    const { visible } = state['features/filmstrip'];
    const participantColor = state['features/base/conference'].colorPicker;

    // Build className more efficiently
    const classNames = [];

    const className = classNames.join(' ');
    const videosClassName = `filmstrip__videos${visible ? '' : ' hidden'}`;
    const { hideHeaderFooter } = state['features/filmstrip'];
    const { gridDimensions = {}, filmstripWidth }
        = state['features/filmstrip'].tileViewDimensions;
    const participants = state['features/base/participants'];
    const filmStripView = state['features/base/conference'].filmStripView;
    const conferenceView = state['features/base/conference'].conferenceView;
    const tileParticipantLimit = state['features/filmstrip'].tileViewTileLimit;
    const conference = state['features/base/conference'].conference;
    const tileViewTileLimit = state['features/filmstrip']?.tileViewTileLimit;
    const electedParticipantID = selectParticipantforLargeViewGrid(state);
    const displayHeaderInRecorder = state['features/base/config'].interfaceConfig.displayHeaderInRecorder;
    const participantsAndSharedThumbnails = getAllParticipantAndSharedFrames(state);
    const isScreenSharing = state['features/base/tracks']?.some(t => t.videoType === 'desktop');

    // A boolean, not the filtered array: a new array on every call failed react-redux's shallow comparison and
    // re-rendered the whole filmstrip on every action.
    const hasYoutubeParticipant = participants.some(
        participant => participant.isFakeParticipant && participant.name === 'YouTube');


    return {
        _className: className,
        _columns: gridDimensions.columns || 2,
        _currentLayout: getCurrentLayout(state),
        _filmStripView: filmStripView,
        _filmstripWidth: filmstripWidth,
        _hideScrollbar: false,
        _hideToolbar: Boolean(iAmSipGateway),
        _isFilmstripButtonEnabled: isButtonEnabled('filmstrip', state),
        _participantsLength: participantsAndSharedThumbnails ? participantsAndSharedThumbnails.length : 0,
        _participants: participants,
        _rows: gridDimensions.rows || 1,
        _videosClassName: videosClassName,
        _visible: visible,
        iAmRecorder: Boolean(iAmRecorder),
        _conferenceView: conferenceView,
        _tileParticipantLimit: tileParticipantLimit,
        _pictureInPictureEnabled: getPictureInPictureModeConfig(state),
        _conference: conference ?? null,
        _singleParticipantColor: participantColor,
        _tileViewTileLimit: tileViewTileLimit,
        hideHeaderFooter,
        _isScreenSharing: isScreenSharing,
        displayHeaderInRecorder,
        _localParticipant: getLocalParticipant(state),
        _hasYoutubeParticipant: hasYoutubeParticipant,
        electedParticipantID

    };
}

export default translate(connect(_mapStateToProps)(Filmstrip));
